import { useQuery } from "@tanstack/react-query"
import { useSelector } from "react-redux"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { sage300ConnectorService } from "@/modules/profile/infrastructure/sage300ConnectorService"
import type { Sage300CompanyDetail } from "@/modules/profile/dtos/Sage300ConnectDto"
import type { RootState } from "@/store"
import { salaryIncreaseService } from "../infrastructure/salary-increase-service"

export interface SageCompanyOption {
  companyCode: string
  companyName: string
}

// company-details returns one row per company + rule, so the same company
// repeats; collapse to one option per companyCode.
function toCompanyOptions(rows: Sage300CompanyDetail[]): SageCompanyOption[] {
  const byCode = new Map<string, SageCompanyOption>()
  for (const row of rows) {
    if (!byCode.has(row.companyCode)) {
      byCode.set(row.companyCode, {
        companyCode: row.companyCode,
        companyName: row.companyName.trim(),
      })
    }
  }
  return [...byCode.values()]
}

export function useSageCompanies(enabled = true) {
  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )

  return useQuery({
    queryKey: ["sage-companies", organizationId],
    queryFn: () => sage300ConnectorService.getCompanyDetails(organizationId!),
    select: toCompanyOptions,
    enabled: enabled && !!organizationId,
  })
}

export function useSageCompanyRules(companyCode: string | undefined) {
  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )

  return useQuery({
    queryKey: ["sage-company-rules", organizationId, companyCode],
    queryFn: () =>
      salaryIncreaseService.getCompanyRules(organizationId!, companyCode!),
    enabled: !!organizationId && !!companyCode,
  })
}
